import cx from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { animated, Keyframes } from 'react-spring'

import type { PageLoginViewModel } from '@/modules/Navigation/view-models/PageLoginViewModel'
import type { NavigationLogoViewModel } from '@/view-models/NavigationLogoViewModel'
import BaseButton from '../BaseButton/BaseButton'
import NavigationLogo from '../NavigationLogo/NavigationLogo'
import NotificationCount from '../NotificationCount/NotificationCount'
import PageLogin from '../PageLogin/PageLogin'
import styles from './headerMobile-style.css'

const Sidebar: any = Keyframes.Spring({
  open: { delay: 0, x: 0 },
  close: async (call) => {
    await call({ delay: 0, x: -100 })
  },
})

// Overlay Component
const Overlay: React.FC<{
  visible: boolean
  content: { links: React.ReactNode; title: string } | null
  onClose: () => void
}> = ({ visible, content, onClose }) => {
  const sidebarState = visible ? 'open' : 'close'
  const overlayRef = useRef<HTMLDivElement>(null)


  useEffect(() => {
    const body = document.getElementsByTagName('body')[0]

    if (visible) {
      setTimeout(() => {
        body.style.overflow = 'hidden'
        body.style.height = '100vh'
        body.style.width = '100vw'
      }, 400)
    } else {
      body.style.overflow = 'visible'
      body.style.width = 'auto'
      body.style.height = 'auto'
    }

    return () => {
      body.style.overflow = 'visible'
      body.style.width = 'auto'
      body.style.height = 'auto'
    }
  }, [visible])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (visible) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [visible, onClose])

  return (
    <>
      <div
        className={cx(styles.overlay, visible && styles.isActive)}
      />
      <div ref={overlayRef}>

        <Sidebar native state={sidebarState}>
          {({ x }) => (
            <animated.div
              className={cx(styles.sideMenu, visible && styles.isOpen)}
              style={{
                transform: x.interpolate((x) => `translate3d(${x}%,0,0)`),
                backgroundColor: 'white',
              }}
            >
              {content && content.links}
            </animated.div>
          )}
        </Sidebar>
      </div>
    </>
  )
}

// PageLoginWrapper Component
const PageLoginWrapper: React.FC<{
  pageLogin?: PageLoginViewModel
  alignLogin?: 'right' | 'left'
  onDropdownLinksUpdate: (links: React.ReactNode, title: string) => void
  onCloseToggle?: () => void
}> = ({ pageLogin, alignLogin, onDropdownLinksUpdate, onCloseToggle }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  if (!pageLogin) return null

  return (
    <div ref={wrapperRef}>
      <PageLogin
        items={pageLogin.links}
        title={pageLogin.title}
        loggedIn={pageLogin.loggedIn}
        align={alignLogin}
        text={pageLogin.text}
        cardLinks={pageLogin.cardLinks}
        onDropdownLinksUpdate={onDropdownLinksUpdate}
      />
    </div>
  )
}

// HeaderMobile Component
const HeaderMobile = ({
                                         logo,
                                         pageLogin,
                                         onToggle,
                                         isOpen,
                                         notificationCount,
                                         alignLogin,
                                         handleCloseToggle,
                        onCloseNavigationOverlay, // New prop

                      }) => {
  const [overlayVisible, setOverlayVisible] = useState(false)
  const [overlayContent, setOverlayContent] = useState<{ links: React.ReactNode; title: string } | null>(null)

  const onDropdownLinksUpdate = (links: React.ReactNode, title: string) => {
    setOverlayVisible((prev) => !prev)
    setOverlayContent({ links, title })
    onCloseNavigationOverlay?.() // Close NavigationMobile overlay
  }

  const handleToggle = () => {
    onToggle?.()
    setOverlayVisible(false)
  }

  return (
    <>
      <div className={styles.headerMobile}>
        <BaseButton onClick={handleToggle} eventMeta={isOpen ? {} : { event: 'burgermenu' }}>
          <div className={cx(styles.burgerHolder, isOpen && styles.open)}>
            <div className={cx(styles.burger, isOpen && styles.open)}>
              <span />
              <span />
              <span />
            </div>
            {notificationCount && <NotificationCount notificationCount={notificationCount} />}
          </div>
        </BaseButton>
        <NavigationLogo {...logo} className={styles.logo_mobile} />
        <PageLoginWrapper
          pageLogin={pageLogin}
          alignLogin={alignLogin}
          onDropdownLinksUpdate={onDropdownLinksUpdate}
          onCloseToggle={handleCloseToggle}
        />
      </div>
      <Overlay visible={overlayVisible} content={overlayContent} onClose={() => setOverlayVisible(false)} />
    </>
  )
}

export default HeaderMobile